import { useState, useEffect } from 'react'
import {
  Play, Pause, Download, RefreshCw, Mic, Sparkles, X, Loader2, Copy,
  Tags, Database, User, AlertTriangle, CheckCircle2, AlertCircle
} from 'lucide-react'
import useSipStore from '../stores/sipStore'
import usePhonebookStore, { normalisePhone, formatPhone } from '../stores/phonebookStore'
import CustomerProfile from './CustomerProfile'

// GCS-backed list endpoint (proxied through agent.kailai.tech/gcs)
const LIST_URL = (domain) => `https://${domain}/gcs/recordings`
// Signed-URL endpoint - 302 redirects to a Google-hosted URL
const SIGN_URL = (domain, objectPath) =>
  `https://${domain}/gcs/recordings/${encodeURIComponent(objectPath)}`
// AI analysis endpoints - all share the same shape: POST { object } → result
const AI_URL = (domain, kind) => `https://${domain}/gcs/${kind}`

function formatBytes(b) {
  if (!b) return '?'
  if (b < 1024) return `${b}B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)}KB`
  return `${(b / (1024 * 1024)).toFixed(1)}MB`
}

function parseFilename(fullPath) {
  // Object key is YYYY/MM/DD/call-20260512-173025-0502298318-1234567890.123.wav
  const base = fullPath.split('/').pop() || fullPath
  const m = base.match(/^call-(\d{8})-(\d{6})-(\d+)/)
  if (!m) return { display: base, date: null, number: null }
  const [_, dateStr, timeStr, number] = m
  const dateFmt = `${dateStr.slice(6, 8)}/${dateStr.slice(4, 6)}/${dateStr.slice(0, 4)}`
  const timeFmt = `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}:${timeStr.slice(4, 6)}`
  return { display: `${number} · ${dateFmt} ${timeFmt}`, number, date: `${dateFmt} ${timeFmt}` }
}

export default function Recordings() {
  const domain = useSipStore(s => s.config.domain)
  const phonebook = usePhonebookStore(s => s.contacts)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [playingUrl, setPlayingUrl] = useState(null)
  const [audio] = useState(() => new Audio())

  // Phone → contact lookup
  const phoneToContact = (() => {
    const m = new Map()
    for (const c of phonebook) for (const p of c.phones) m.set(p, c)
    return m
  })()
  // Per-(object, kind) AI state - results[object][kind] = { status, data }
  const [results, setResults] = useState({})
  // What's currently shown in the modal: { object, kind } or null
  const [openModal, setOpenModal] = useState(null)
  // Customer profile modal: phone number or null
  const [openProfile, setOpenProfile] = useState(null)

  const runAi = async (objectName, kind) => {
    setResults(r => ({
      ...r,
      [objectName]: { ...(r[objectName] || {}), [kind]: { status: 'loading' } },
    }))
    setOpenModal({ object: objectName, kind })
    try {
      const res = await fetch(AI_URL(domain, kind), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ object: objectName }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`)
      const data = await res.json()
      setResults(r => ({
        ...r,
        [objectName]: { ...(r[objectName] || {}), [kind]: { status: 'done', data } },
      }))
    } catch (e) {
      setResults(r => ({
        ...r,
        [objectName]: { ...(r[objectName] || {}), [kind]: { status: 'error', error: e.message } },
      }))
    }
  }

  // Extract the phone number from a recording filename.
  // Newer format includes the agent extension:
  //   call-YYYYMMDD-HHMMSS-AGENT-PHONE-UNIQUEID.wav  (5+ groups)
  // Older format (pre-multi-user):
  //   call-YYYYMMDD-HHMMSS-PHONE-UNIQUEID.wav        (4 groups)
  // Either way the EXTERNAL phone is the longest digit run that isn't
  // a 4-digit agent extension (1000-1099).
  const phoneOf = (objectName) => {
    const base = objectName.split('/').pop() || ''
    // Strip prefix + .wav, then split on '-' to get the parts
    const m = base.match(/^call-\d{8}-\d{6}-(.+)\.wav$/)
    if (!m) return null
    const parts = m[1].split('-')
    // Find the part that looks like a real phone number (≥ 7 digits and not
    // a SIP extension 10XX)
    for (const p of parts) {
      const digits = p.replace(/[^\d]/g, '')
      if (digits.length >= 7 && !/^10\d{2}$/.test(digits)) return digits
    }
    return null
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(LIST_URL(domain), { credentials: 'omit' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      // GCS proxy returns [{name, size, updated, contentType}, ...]
      const wavs = (Array.isArray(data) ? data : []).filter(f => f.name?.endsWith('.wav'))
      setItems(wavs)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    const interval = setInterval(load, 15000)
    return () => clearInterval(interval)
  }, [domain])

  useEffect(() => {
    return () => audio.pause()
  }, [audio])

  const play = (url) => {
    if (playingUrl === url) {
      audio.pause()
      setPlayingUrl(null)
      return
    }
    audio.src = url
    audio.play()
      .then(() => setPlayingUrl(url))
      .catch(() => setPlayingUrl(null))
    audio.onended = () => setPlayingUrl(null)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-text flex items-center gap-2">
          <Mic size={16} className="text-rose-400" />
          הקלטות <span className="text-xs text-mute">({items.length})</span>
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className="text-mute hover:text-text p-1 disabled:opacity-50"
          title="רענן"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="text-xs text-err mb-2 ltr tech">{error}</div>
      )}

      {items.length === 0 && !loading && !error && (
        <div className="text-center text-mute py-8 text-sm">
          <Mic size={28} className="mx-auto mb-2 opacity-40" />
          אין הקלטות
          <div className="text-xs mt-1">הפעל "הקלט שיחה" לפני חיוג</div>
        </div>
      )}

      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {items.map(item => {
          const url = SIGN_URL(domain, item.name)
          const meta = parseFilename(item.name)
          const isPlaying = playingUrl === url
          const r = results[item.name] || {}
          const phone = phoneOf(item.name)
          return (
            <div
              key={item.name}
              className="flex items-center gap-1 px-2 py-2 rounded-lg hover:bg-line/30 group"
            >
              <button
                onClick={() => play(url)}
                className={`p-2 rounded-lg shrink-0 ${
                  isPlaying ? 'bg-brand text-slate-900' : 'bg-line/40 hover:bg-line/60 text-content'
                }`}
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <div className="flex-1 min-w-0">
                {(() => {
                  const contact = phone ? phoneToContact.get(normalisePhone(phone)) : null
                  return contact ? (
                    <>
                      <div className="text-sm font-medium truncate flex items-center gap-1">
                        <User size={10} className="text-brand shrink-0" />
                        {contact.name}
                      </div>
                      <div className="text-[11px] text-mute ltr tech truncate">{meta.display}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm tech ltr truncate">{meta.display}</div>
                      <div className="text-xs text-mute">{formatBytes(item.size)}</div>
                    </>
                  )
                })()}
              </div>

              {/* AI: Transcribe */}
              <AiButton
                state={r.transcribe} done={r.transcribe?.status === 'done'}
                onClick={() => r.transcribe?.status === 'done'
                  ? setOpenModal({ object: item.name, kind: 'transcribe' })
                  : runAi(item.name, 'transcribe')}
                icon={<Sparkles size={13} />}
                color="purple"
                title="תמלול AI"
              />

              {/* AI: Tag */}
              <AiButton
                state={r.tag} done={r.tag?.status === 'done'}
                onClick={() => r.tag?.status === 'done'
                  ? setOpenModal({ object: item.name, kind: 'tag' })
                  : runAi(item.name, 'tag')}
                icon={<Tags size={13} />}
                color="amber"
                title="תיוג"
              />

              {/* AI: Extract */}
              <AiButton
                state={r.extract} done={r.extract?.status === 'done'}
                onClick={() => r.extract?.status === 'done'
                  ? setOpenModal({ object: item.name, kind: 'extract' })
                  : runAi(item.name, 'extract')}
                icon={<Database size={13} />}
                color="cyan"
                title="חילוץ מידע"
              />

              {/* Customer profile (cross-call summary) */}
              {phone && (
                <button
                  onClick={() => setOpenProfile(phone)}
                  className="p-1.5 rounded shrink-0 text-mute hover:text-brand hover:bg-brand/10"
                  title={`פרופיל לקוח: ${phone}`}
                >
                  <User size={13} />
                </button>
              )}

              <a
                href={url}
                download={item.name}
                className="opacity-0 group-hover:opacity-100 p-1.5 text-mute hover:text-text shrink-0"
                title="הורד"
              >
                <Download size={13} />
              </a>
            </div>
          )
        })}
      </div>

      {/* Modal for transcript / tag / extract */}
      {openModal && (
        <AnalysisModal
          objectName={openModal.object}
          kind={openModal.kind}
          state={results[openModal.object]?.[openModal.kind]}
          onClose={() => setOpenModal(null)}
          onRerun={() => runAi(openModal.object, openModal.kind)}
        />
      )}

      {openProfile && (
        <CustomerProfile phone={openProfile} onClose={() => setOpenProfile(null)} />
      )}
    </div>
  )
}

function AiButton({ state, done, onClick, icon, color, title }) {
  const status = state?.status
  const colorMap = {
    purple: { done: 'text-purple-400 hover:bg-purple-500/20',
              idle: 'text-mute hover:text-purple-400 hover:bg-purple-500/10' },
    amber:  { done: 'text-amber-300 hover:bg-amber-500/20',
              idle: 'text-mute hover:text-amber-300 hover:bg-amber-500/10' },
    cyan:   { done: 'text-cyan-300 hover:bg-cyan-500/20',
              idle: 'text-mute hover:text-cyan-300 hover:bg-cyan-500/10' },
  }
  const cls = status === 'error' ? 'text-rose-400 hover:bg-rose-500/20'
            : done                ? colorMap[color].done
            : colorMap[color].idle
  return (
    <button
      onClick={onClick}
      disabled={status === 'loading'}
      title={status === 'error' ? `שגיאה: ${state.error}` : status === 'loading' ? 'מנתח…' : done ? `${title} - הצג` : title}
      className={`p-1.5 rounded shrink-0 transition-colors ${cls}`}
    >
      {status === 'loading' ? <Loader2 size={13} className="animate-spin" /> : icon}
    </button>
  )
}

const KIND_CONFIG = {
  transcribe: {
    title: 'תמלול AI',
    Icon: Sparkles,
    color: 'text-purple-400',
    accent: 'bg-purple-500/10 border-purple-500/30',
  },
  tag: {
    title: 'תיוג אוטומטי',
    Icon: Tags,
    color: 'text-amber-300',
    accent: 'bg-amber-500/10 border-amber-500/30',
  },
  extract: {
    title: 'חילוץ מידע',
    Icon: Database,
    color: 'text-cyan-300',
    accent: 'bg-cyan-500/10 border-cyan-500/30',
  },
}

function AnalysisModal({ objectName, kind, state, onClose, onRerun }) {
  const [copied, setCopied] = useState(null)
  const meta = parseFilename(objectName)
  const cfg = KIND_CONFIG[kind]
  const Icon = cfg.Icon

  const copy = (text, key) => {
    navigator.clipboard.writeText(text)
    setCopied(key)
    setTimeout(() => setCopied(null), 1500)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-panel border border-line rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-line">
          <div>
            <div className="font-semibold flex items-center gap-2">
              <Icon size={16} className={cfg.color} />
              {cfg.title}
            </div>
            <div className="text-xs text-mute mt-0.5 ltr tech">{meta.display}</div>
          </div>
          <div className="flex items-center gap-2">
            {state?.status === 'done' && (
              <button
                onClick={onRerun}
                className="text-mute hover:text-text text-xs px-2 py-1 rounded hover:bg-line/30"
                title="הרץ מחדש"
              >
                <RefreshCw size={14} />
              </button>
            )}
            <button onClick={onClose} className="text-mute hover:text-text">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-4 space-y-4">
          {state?.status === 'loading' && (
            <div className="text-center py-12 text-mute">
              <Loader2 size={28} className={`mx-auto mb-3 animate-spin ${cfg.color}`} />
              <div className="text-sm">Gemini מנתח את ההקלטה…</div>
            </div>
          )}

          {state?.status === 'error' && (
            <div className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-3 text-sm">
              <div className="font-medium text-rose-300 mb-1">שגיאה</div>
              <div className="text-xs text-rose-200 ltr tech">{state.error}</div>
              <button onClick={onRerun} className="mt-2 text-xs underline text-rose-300">נסה שוב</button>
            </div>
          )}

          {state?.status === 'done' && kind === 'transcribe' && (
            <TranscribeBody data={state.data} copy={copy} copied={copied} cfg={cfg} />
          )}
          {state?.status === 'done' && kind === 'tag' && (
            <TagBody data={state.data} cfg={cfg} />
          )}
          {state?.status === 'done' && kind === 'extract' && (
            <ExtractBody data={state.data} cfg={cfg} />
          )}

          {state?.status === 'done' && state.data.model && (
            <div className="text-[10px] text-mute text-center ltr tech opacity-60">
              {state.data.model} · {new Date(state.data.analyzedAt || state.data.transcribedAt || Date.now()).toLocaleString('he-IL')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TranscribeBody({ data, copy, copied, cfg }) {
  return (
    <>
      {data.summary && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm">סיכום</h3>
            <button onClick={() => copy(data.summary, 'summary')} className="text-xs text-mute hover:text-text flex items-center gap-1">
              <Copy size={12} />{copied === 'summary' ? 'הועתק!' : 'העתק'}
            </button>
          </div>
          <div className={`rounded-lg p-3 text-sm leading-relaxed whitespace-pre-wrap ${cfg.accent}`}>
            {data.summary}
          </div>
        </section>
      )}
      {data.transcript && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm">תמלול מלא</h3>
            <button onClick={() => copy(data.transcript, 'transcript')} className="text-xs text-mute hover:text-text flex items-center gap-1">
              <Copy size={12} />{copied === 'transcript' ? 'הועתק!' : 'העתק'}
            </button>
          </div>
          <div className="bg-bg/60 border border-line rounded-lg p-3 text-sm leading-relaxed whitespace-pre-wrap">
            {data.transcript}
          </div>
        </section>
      )}
    </>
  )
}

function Pill({ label, value, color }) {
  if (!value) return null
  const colors = {
    emerald: 'bg-emerald-500/20 text-emerald-300',
    rose:    'bg-rose-500/20 text-rose-300',
    amber:   'bg-amber-500/20 text-amber-300',
    cyan:    'bg-cyan-500/20 text-cyan-300',
    line:    'bg-line/40 text-text',
  }
  return (
    <div>
      <div className="text-[10px] text-mute mb-0.5">{label}</div>
      <span className={`inline-block px-2 py-1 rounded text-xs ${colors[color] || colors.line}`}>{value}</span>
    </div>
  )
}

function TagBody({ data, cfg }) {
  const sentimentColor = data.sentiment === 'חיובי' ? 'emerald'
                      : data.sentiment === 'שלילי' ? 'rose'
                      : 'line'
  const outcomeColor = data.outcome === 'הצליחה' ? 'emerald'
                     : data.outcome === 'נכשלה'  ? 'rose'
                     : 'amber'
  const urgencyColor = data.urgency === 'דחוף' ? 'rose'
                     : data.urgency === 'המתנה' ? 'amber'
                     : 'line'
  return (
    <>
      <div className={`rounded-lg p-3 ${cfg.accent}`}>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Pill label="קטגוריה" value={data.category} color="amber" />
          <Pill label="תת-קטגוריה" value={data.subcategory} color="line" />
          <Pill label="רגש" value={data.sentiment} color={sentimentColor} />
          <Pill label="תוצאה" value={data.outcome} color={outcomeColor} />
          <Pill label="דחיפות" value={data.urgency} color={urgencyColor} />
          <Pill label="איכות נציג" value={data.agent_quality} color="line" />
        </div>
      </div>
      {data.tags?.length > 0 && (
        <section>
          <div className="text-xs text-mute mb-1">תוויות</div>
          <div className="flex flex-wrap gap-1.5">
            {data.tags.map((t, i) => (
              <span key={i} className="px-2 py-1 rounded bg-amber-500/10 text-amber-200 text-xs">{t}</span>
            ))}
          </div>
        </section>
      )}
    </>
  )
}

function ExtractBody({ data, cfg }) {
  const hasAny = data.customer_name || data.customer_id || data.email
                 || data.phone_numbers?.length || data.addresses?.length
                 || data.amounts?.length || data.dates?.length
                 || data.products?.length || data.key_facts?.length
                 || data.action_items?.length
  if (!hasAny) {
    return <div className="text-center text-mute py-4 text-sm">לא נמצא מידע מובנה לחילוץ</div>
  }

  const Item = ({ label, children }) => (
    <div>
      <div className="text-[10px] text-mute mb-1">{label}</div>
      {children}
    </div>
  )

  return (
    <div className={`rounded-lg p-3 space-y-3 ${cfg.accent}`}>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {data.customer_name && <Item label="שם לקוח"><div>{data.customer_name}</div></Item>}
        {data.customer_id && <Item label="ת.ז."><div className="ltr tech">{data.customer_id}</div></Item>}
        {data.email && <Item label="אימייל"><div className="ltr tech text-cyan-300">{data.email}</div></Item>}
      </div>

      {data.phone_numbers?.length > 0 && (
        <Item label="מספרי טלפון נוספים">
          <div className="flex flex-wrap gap-1.5">
            {data.phone_numbers.map((p, i) => (
              <span key={i} className="px-2 py-1 rounded bg-cyan-500/10 text-cyan-200 text-xs ltr tech">{p}</span>
            ))}
          </div>
        </Item>
      )}

      {data.amounts?.length > 0 && (
        <Item label="סכומים">
          <div className="space-y-1">
            {data.amounts.map((a, i) => (
              <div key={i} className="text-xs flex gap-2">
                <span className="tech ltr font-medium">{a.value} {a.currency || 'ILS'}</span>
                <span className="text-mute">— {a.context}</span>
              </div>
            ))}
          </div>
        </Item>
      )}

      {data.dates?.length > 0 && (
        <Item label="תאריכים">
          <div className="space-y-1">
            {data.dates.map((d, i) => (
              <div key={i} className="text-xs flex gap-2">
                <span className="tech ltr">{d.date}</span>
                <span className="text-mute">— {d.context}</span>
              </div>
            ))}
          </div>
        </Item>
      )}

      {data.products?.length > 0 && (
        <Item label="מוצרים / שירותים">
          <div className="flex flex-wrap gap-1.5">
            {data.products.map((p, i) => (
              <span key={i} className="px-2 py-1 rounded bg-line/40 text-xs">{p}</span>
            ))}
          </div>
        </Item>
      )}

      {data.addresses?.length > 0 && (
        <Item label="כתובות">
          <ul className="text-xs space-y-0.5 list-disc list-inside marker:text-cyan-400">
            {data.addresses.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Item>
      )}

      {data.key_facts?.length > 0 && (
        <Item label="עובדות מפתח">
          <ul className="text-xs space-y-0.5 list-disc list-inside marker:text-cyan-400">
            {data.key_facts.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Item>
      )}

      {data.action_items?.length > 0 && (
        <Item label="מטלות להמשך">
          <ul className="text-xs space-y-0.5 list-disc list-inside marker:text-amber-400">
            {data.action_items.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Item>
      )}
    </div>
  )
}
